import { PaginationControls } from '@droidmap/shared-service-contract'

import { Role } from './role.js'

export enum GrantResourceType {
  ORGANISATION = 'organisation',
  PROJECT = 'project',
}

export enum GrantPrincipalType {
  USER = 'user',
  GROUP = 'group',
}

export type Grant = {
  /**
   * The id of the organisation we are defining a grant for.
   *
   * Regardless of the resource type, the orgId is always added to
   * make it easier to query grants by organisation.
   */
  orgId: string

  /**
   * The id of the resource we are defining a grant for.
   */
  resourceId: string

  /**
   * The type of the resource (organisation or project)
   */
  resourceType: GrantResourceType

  /**
   * The id for the principal we are granting access to the resource for.
   */
  principalId: string

  /**
   * The type of the principal (user or group)
   */
  principalType: GrantPrincipalType

  /**
   * The role that the principal has been assigned for this resource.
   */
  role: Role

  /**
   * The time the entity was created. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  created: Date

  /**
   * The user id of the user who created this entity.
   */
  createdBy: string

  /**
   * The last time this entity was modified. This attribute is automatically
   * set by the dynamodb-onetable library.
   */
  updated: Date

  /**
   * The user id of the user who last updated this entity.
   */
  updatedBy: string
}

export type CreateGrantInput = Omit<
  Grant,
  'created' | 'updated' | 'createdBy' | 'updatedBy'
>

export type GetGrantInput = {
  resourceId: string
  principalId: string
  role: string
}

export type DeleteGrantInput = {
  resourceId: string
  resourceType: GrantResourceType
  principalId: string
  role: Role
}

/**
 * Request body for the internal "bootstrap an organisation's grants" endpoint
 * ({@link PermissionsServiceClient.bootstrapOrganisationGrants}). Used by the
 * organisation-service to create the org's initial `ORG_ADMIN` grants
 * synchronously as part of org creation, rather than waiting for the
 * `Organisation Created` event to be processed asynchronously.
 *
 * The creator always gets a `USER`-principal `ORG_ADMIN` grant. When
 * `administratorsGroupId` is supplied, the `Administrators` group also gets a
 * `GROUP`-principal `ORG_ADMIN` grant; the group must already exist with the
 * creator as a member, so the caller creates it before invoking this endpoint.
 */
/**
 * Request body for the internal "bootstrap a new member's grants" endpoint
 * ({@link PermissionsServiceClient.bootstrapOrganisationMemberGrants}): the
 * baseline `ORG_READ` grant a member gets on joining an organisation by
 * accepting an invitation (#3062), so they can see and enter it before an
 * admin adds them to groups.
 */
export type BootstrapOrganisationMemberGrantsInput = {
  orgId: string
  userId: string
}

export type BootstrapOrganisationGrantsInput = {
  orgId: string
  organisationCreatorId: string
  /**
   * The id of the org's `Administrators` group, which is granted the
   * `ORG_ADMIN` role so future members inherit organisation-admin access.
   *
   * Optional for backward compatibility across a rolling deploy: a caller
   * running an older organisation-service omits it, in which case only the
   * creator's grant is bootstrapped (the group grant is then filled in once
   * both services are on the new version). New callers always send it.
   */
  administratorsGroupId?: string
}

/**
 * The roles that can be granted on a project. Org roles (`ORG_*`) are granted
 * on the organisation, never directly on a project by a caller.
 */
export const PROJECT_ROLES = [
  Role.PROJECT_ADMIN,
  Role.PROJECT_WRITE,
  Role.PROJECT_READ,
] as const

export type ProjectRole = (typeof PROJECT_ROLES)[number]

/**
 * Upper bound on the grants a caller can attach to a project at creation
 * (#3077). Each group grant fans out to every member inside the create
 * request, so this keeps a single create bounded.
 */
export const MAX_INITIAL_PROJECT_GRANTS = 50

/**
 * A grant to create on a project as part of creating it (#3077): a user or
 * group of the project's organisation, given one of the {@link PROJECT_ROLES}.
 */
export type InitialProjectGrant = {
  principalId: string
  principalType: GrantPrincipalType
  role: ProjectRole
}

/**
 * Request body for the internal "bootstrap a project's grants" endpoint
 * ({@link PermissionsServiceClient.bootstrapProjectGrants}). Used by the
 * project-service to create the creator's `PROJECT_ADMIN` grant and copy the
 * org's `ORG_ADMIN` grants down to the project synchronously as part of project
 * creation, rather than waiting for the `Project Created` event.
 */
export type BootstrapProjectGrantsInput = {
  orgId: string
  projectId: string
  projectCreatorId: string
  /**
   * Grants the creator chose when creating the project (#3077), created in the
   * same bootstrap call so they exist atomically with the project. The caller
   * has already validated every principal against the org.
   *
   * Optional for backward compatibility across a rolling deploy: an older
   * project-service omits it and gets the bootstrap grants only.
   */
  additionalGrants?: InitialProjectGrant[]
}

export type ListGrantsByResourceInput = {
  resourceId: string
} & PaginationControls

/**
 * How a user came to hold `ORG_ADMIN` on an organisation: a `USER`-principal
 * grant of their own (`direct`), or membership of a group that holds a
 * `GROUP`-principal grant (`group`).
 */
export type OrgAdminAccessSource =
  { type: 'direct' } | { type: 'group'; groupId: string }

/**
 * One user who can access a resource because they are an admin of its
 * organisation (#3085), with every route by which they hold that role. A user
 * who is both a direct admin and in an admin group appears once, with both
 * sources.
 */
export type OrgAdminAccess = {
  userId: string
  sources: OrgAdminAccessSource[]
}

export type ListOrgAdminAccessInput = {
  /** The resource (typically a project) whose organisation's admins to list. */
  resourceId: string
}

export type ListGrantsByPrincipalInput = {
  principalId: string
} & PaginationControls
